import React, { useState, useEffect } from 'react';
import { Calendar, Clock, CheckCircle, XCircle, TrendingUp, Users, Loader, ChevronLeft, ChevronRight, ChevronDown, User } from 'lucide-react';
import Layout from '../../Layout';
import ParentAPI from '../../services/ParentAPI';
import { showErrorNotification } from '../../utils/errorHandler';

const LearnerAttendance = () => {
    const [selectedChild, setSelectedChild] = useState(null);
    const [children, setChildren] = useState([]);
    const [attendanceData, setAttendanceData] = useState([]);
    const [attendanceStats, setAttendanceStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);

    useEffect(() => {
        loadChildren();
    }, []);

    useEffect(() => {
        if (selectedChild) {
            loadAttendanceData();
        }
    }, [selectedChild, currentDate]);

    const loadChildren = async () => {
        try {
            const childrenData = await ParentAPI.getChildren();
            setChildren(childrenData);
            
            if (childrenData.length > 0) {
                setSelectedChild(childrenData[0]);
            }
        } catch (err) {
            console.error('Error loading children:', err);
            showErrorNotification('Failed to load children data');
        } finally {
            setLoading(false);
        }
    };

    const loadAttendanceData = async () => {
        if (!selectedChild) return;

        try {
            setLoading(true);
            const [attendance, stats] = await Promise.all([
                ParentAPI.getChildAttendance(selectedChild.student_id),
                ParentAPI.getAttendanceStats(
                    selectedChild.student_id, 
                    currentDate.getMonth(), 
                    currentDate.getFullYear()
                )
            ]);

            setAttendanceData(attendance);
            setAttendanceStats(stats);
        } catch (err) {
            console.error('Error loading attendance data:', err);
            showErrorNotification('Failed to load attendance data');
        } finally {
            setLoading(false);
        }
    };

    const handleChildSwitch = (childId) => {
        const child = children.find(c => c.student_id === childId);
        setSelectedChild(child);
    };

    const navigateMonth = (direction) => {
        const newDate = new Date(currentDate);
        newDate.setMonth(currentDate.getMonth() + direction);
        setCurrentDate(newDate);
    };

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days = [];
        
        // Add empty cells for days before the first day of the month
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
        }
        
        // Add all days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            days.push(day);
        }
        
        return days;
    };

    const getAttendanceForDay = (day) => {
        if (!attendanceStats?.dailyData || !day) return null;
        return attendanceStats.dailyData[day];
    };

    const formatDate = (date) => {
        return date.toLocaleDateString('en-US', { 
            month: 'long', 
            year: 'numeric' 
        });
    };

    if (loading && !selectedChild) {
        return (
            <Layout pageTitle="Learner's Attendance">
                <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 flex items-center justify-center">
                    <div className="text-center">
                        <Loader className="w-12 h-12 animate-spin mx-auto mb-4 text-green-600" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Loading Attendance</h2>
                        <p className="text-gray-600">Please wait while we fetch attendance information...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!selectedChild) {
        return (
            <Layout pageTitle="Learner's Attendance">
                <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 flex items-center justify-center">
                    <div className="text-center">
                        <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">No Child Selected</h2>
                        <p className="text-gray-600">Please select a child to view attendance information.</p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout pageTitle="Learner's Attendance">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
                
                .card-parent {
                    border: 1px solid #e5e7eb;
                    background: white;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                }
                
                .card-parent:hover {
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
                }
                
                .gradient-text {
                    background: linear-gradient(135deg, #059669, #3b82f6);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }
            `}</style>

            <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 font-['Poppins',sans-serif]">
                {/* Header */}
                <header className="bg-gradient-to-r from-green-600 to-blue-600 shadow-2xl">
                    <div className="max-w-7xl mx-auto px-6 py-6">
                        <div className="flex items-center justify-between flex-wrap gap-4">
                            <div className="flex items-center space-x-4">
                                <div>
                                    <Calendar className="w-12 h-12 text-white"/>
                                </div>
                                <div>
                                    <h1 className="text-3xl font-bold text-white">
                                        {selectedChild ? `${selectedChild.name}'s Attendance` : "Learner's Attendance"}
                                    </h1>
                                    <p className="text-green-100">
                                        Track attendance patterns and statistics
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-4">
                                <div className="relative">
                                    <select
                                        value={selectedChild?.student_id || ''}
                                        onChange={(e) => handleChildSwitch(e.target.value)}
                                        className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium border border-white/30 appearance-none cursor-pointer pr-10"
                                        disabled={!children.length}
                                    >
                                        {children.length === 0 ? (
                                            <option value="" className="text-gray-900">No children linked</option>
                                        ) : (
                                            children.map(child => (
                                                <option key={child.student_id} value={child.student_id} className="text-gray-900">
                                                    {child.name} - Grade {child.grade || 'N/A'}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                    <ChevronDown className="w-4 h-4 text-white absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"/>
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                <div className="max-w-7xl mx-auto px-6 py-8">

                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                        {/* Statistics Cards */}
                        <div className="lg:col-span-1 space-y-6">
                            {/* Attendance Rate */}
                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <div className="flex items-center mb-4">
                                    <div className="bg-green-500 p-3 rounded-xl mr-4">
                                        <TrendingUp className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-800">Attendance Rate</h3>
                                        <p className="text-sm text-gray-600">This month</p>
                                    </div>
                                </div>
                                <div className="text-center">
                                    <div className="text-4xl font-bold text-green-600 mb-2">
                                        {attendanceStats?.attendanceRate || 0}%
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-3">
                                        <div 
                                            className="bg-gradient-to-r from-green-400 to-green-600 h-3 rounded-full transition-all duration-500"
                                            style={{ width: `${attendanceStats?.attendanceRate || 0}%` }}
                                        ></div>
                                    </div>
                                </div>
                            </div>

                            {/* Present Days */}
                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <div className="flex items-center mb-4">
                                    <div className="bg-blue-500 p-3 rounded-xl mr-4">
                                        <CheckCircle className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-800">Present Days</h3>
                                        <p className="text-sm text-gray-600">This month</p>
                                    </div>
                                </div>
                                <div className="text-3xl font-bold text-blue-600">
                                    {attendanceStats?.presentDays || 0}
                                </div>
                            </div>

                            {/* Absent Days */}
                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <div className="flex items-center mb-4">
                                    <div className="bg-red-500 p-3 rounded-xl mr-4">
                                        <XCircle className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-800">Absent Days</h3>
                                        <p className="text-sm text-gray-600">This month</p>
                                    </div>
                                </div>
                                <div className="text-3xl font-bold text-red-600">
                                    {attendanceStats?.absentDays || 0}
                                </div>
                            </div>

                            {/* Total Days */}
                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <div className="flex items-center mb-4">
                                    <div className="bg-purple-500 p-3 rounded-xl mr-4">
                                        <Calendar className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-800">Total Days</h3>
                                        <p className="text-sm text-gray-600">This month</p>
                                    </div>
                                </div>
                                <div className="text-3xl font-bold text-purple-600">
                                    {attendanceStats?.totalDays || 0}
                                </div>
                            </div>
                        </div>

                        {/* Calendar */}
                        <div className="lg:col-span-3">
                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                {/* Calendar Header */}
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="text-2xl font-bold text-gray-800">
                                        {formatDate(currentDate)}
                                    </h3>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => navigateMonth(-1)}
                                            className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                                        >
                                            <ChevronLeft className="w-5 h-5 text-gray-600" />
                                        </button>
                                        <button
                                            onClick={() => navigateMonth(1)}
                                            className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                                        >
                                            <ChevronRight className="w-5 h-5 text-gray-600" />
                                        </button>
                                    </div>
                                </div>

                                {/* Calendar Grid */}
                                <div className="grid grid-cols-7 gap-2 mb-4">
                                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                                        <div key={day} className="text-center text-sm font-bold text-gray-500 py-2">
                                            {day}
                                        </div>
                                    ))}
                                </div>

                                <div className="grid grid-cols-7 gap-2">
                                    {getDaysInMonth(currentDate).map((day, index) => {
                                        if (!day) {
                                            return <div key={index} className="h-12"></div>;
                                        }

                                        const attendanceInfo = getAttendanceForDay(day);
                                        let className = 'h-12 flex items-center justify-center rounded-lg text-sm font-medium cursor-pointer transition-all duration-200 ';
                                        
                                        if (attendanceInfo) {
                                            if (attendanceInfo.type === 'present') {
                                                className += 'bg-green-500 text-white hover:bg-green-600 shadow-lg';
                                            } else if (attendanceInfo.type === 'absent') {
                                                className += 'bg-red-500 text-white hover:bg-red-600 shadow-lg';
                                            }
                                        } else {
                                            className += 'bg-gray-100 text-gray-600 hover:bg-gray-200';
                                        }

                                        return (
                                            <div
                                                key={day}
                                                className={className}
                                                onClick={() => setSelectedDate(day)}
                                            >
                                                {day}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Legend */}
                                <div className="flex items-center justify-center gap-6 mt-6 pt-6 border-t border-gray-200">
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 bg-green-500 rounded"></div>
                                        <span className="text-sm text-gray-600">Present</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 bg-red-500 rounded"></div>
                                        <span className="text-sm text-gray-600">Absent</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 bg-gray-300 rounded"></div>
                                        <span className="text-sm text-gray-600">No Data</span>
                                    </div>
                                </div>

                                {/* Selected Date Details */}
                                {selectedDate && (
                                    <div className="mt-6 p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                                        <h4 className="font-bold text-blue-900 mb-2">
                                            Details for {formatDate(currentDate).split(' ')[0]} {selectedDate}
                                        </h4>
                                        {getAttendanceForDay(selectedDate) ? (
                                            <div>
                                                <p className="text-sm text-blue-800">
                                                    Status: {getAttendanceForDay(selectedDate).status}
                                                </p>
                                                <p className="text-sm text-blue-800">
                                                    Note: {getAttendanceForDay(selectedDate).note}
                                                </p>
                                            </div>
                                        ) : (
                                            <p className="text-sm text-gray-600">No attendance data available for this date.</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default LearnerAttendance;